import { useState, useEffect } from 'react';
import { VENTAS_STORAGE_KEY, DEFAULT_SALES_SUMMARY, API_URL } from '../constants';
import {
  authFetch,
  normalizarTexto,
  normalizarTelefonoVisual,
  crearFormularioVentaInicial,
  geocodificarResumenVenta as geocodificarResumenVentaFrontend,
} from '../utils';

function iaDijoCoberturaOk(mensajes = []) {
  return mensajes.some((m) =>
    /ten[eÃ©]s cobertura|tienes cobertura|cobertura ok|hay cobertura|cobertura disponible/i.test(
      String(m?.contenido || '')
    )
  );
}

function normalizarResumen(resumen = {}) {
  return {
    ...DEFAULT_SALES_SUMMARY,
    ...(resumen || {}),
  };
}

function inferirEtapaVenta({ resumen, coberturaOk, savedEtapa }) {
  if (savedEtapa && savedEtapa !== 'Lead nuevo') return savedEtapa;

  const tieneDatosMinimos = Boolean(
    resumen?.nombre_apellido &&
      resumen?.telefono &&
      resumen?.cuil &&
      resumen?.direccion &&
      resumen?.localidad &&
      resumen?.plan &&
      resumen?.precio &&
      resumen?.medio_pago
  );

  if (tieneDatosMinimos) return 'AceptÃ³ oferta';
  if (resumen?.plan && resumen?.precio && resumen?.medio_pago) return 'Propuesta enviada';
  if (coberturaOk || resumen?.direccion || resumen?.localidad) return 'Cobertura validada';
  if (resumen?.nombre_apellido || resumen?.telefono || resumen?.cuil) return 'Calificado';
  return savedEtapa || 'Lead nuevo';
}

function inferirProximaAccion({ etapa, coberturaOk, savedProximaAccion }) {
  if (savedProximaAccion && savedProximaAccion !== 'Validar cobertura') return savedProximaAccion;
  if (etapa === 'AceptÃ³ oferta') return 'Revisar resumen interno';
  if (etapa === 'Propuesta enviada') return 'Registrar aceptaciÃ³n';
  if (etapa === 'Cobertura validada') return 'Enviar propuesta';
  if (coberturaOk) return 'Revisar resumen interno';
  if (etapa === 'Calificado') return 'Validar cobertura';
  if (etapa === 'Venta confirmada') return 'Pendiente de instalaciÃ³n';
  return savedProximaAccion || 'Revisar resumen interno';
}

export default function useVentas({
  conversacionActiva,
  mensajes,
  datosCliente,
  estadoOperativoBackend,
  guardarEstadoOperativo,
  agentes,
  agenteActualId,
  currentUser,
  cargarConversaciones,
  limpiarConversacionActiva,
}) {
  const [ventasLocalData, setVentasLocalData] = useState({});
  const [salesModal, setSalesModal] = useState({ open: false, type: null });
  const [salesModalForm, setSalesModalForm] = useState({});
  const [submittingSalesModal, setSubmittingSalesModal] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(VENTAS_STORAGE_KEY);
      if (saved) setVentasLocalData(JSON.parse(saved));
    } catch (e) {
      console.error('Error leyendo ventas local storage:', e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VENTAS_STORAGE_KEY, JSON.stringify(ventasLocalData));
    } catch (e) {
      console.error('Error guardando ventas local storage:', e);
    }
  }, [ventasLocalData]);

  const getNombreAgente = () =>
    agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente';

  const actualizarVentaLocal = (convId, patch) => {
    if (!convId) return;
    setVentasLocalData((prev) => ({
      ...prev,
      [convId]: {
        ...(prev[convId] || {}),
        ...patch,
      },
    }));
  };

  const construirResumenBase = (conv, local = null) => {
    const resumenGuardado = normalizarResumen(local?.resumenInterno);
    return crearFormularioVentaInicial({
      conversacion: conv,
      mensajes,
      datosCliente,
      ventaActual: local,
      resumenGuardado,
      agenteNombre: getNombreAgente(),
    });
  };

  const cargarContextoCatalogoVentas = async (localidad = '') => {
    const zona = normalizarTexto(localidad);
    if (!zona) {
      return {
        contexto_catalogo: '',
        regla_facturacion_catalogo: '',
        productos_count_catalogo: 0,
        catalogo_error: 'Sin localidad para consultar catálogo',
      };
    }

    try {
      const res = await authFetch(`${API_URL}/api/catalogo/contexto-ia?zona=${encodeURIComponent(zona)}`);
      const data = await res.json();

      if (!res.ok) {
        return {
          contexto_catalogo: '',
          regla_facturacion_catalogo: '',
          productos_count_catalogo: 0,
          catalogo_error: data?.error || 'No se pudo cargar catálogo',
        };
      }

      return {
        contexto_catalogo: data?.contexto || '',
        regla_facturacion_catalogo: data?.reglaFacturacion || '',
        productos_count_catalogo: data?.productosCount || 0,
        catalogo_error: '',
      };
    } catch (error) {
      console.error('[VENTAS] Error cargando catálogo comercial:', error);
      return {
        contexto_catalogo: '',
        regla_facturacion_catalogo: '',
        productos_count_catalogo: 0,
        catalogo_error: 'Error de conexión cargando catálogo',
      };
    }
  };

  const obtenerVentaLocal = (conv) => {
    if (!conv?.id) return null;
    const saved = ventasLocalData[conv.id] || {};
    const auto = construirResumenBase(conv, saved);
    const resumenInterno = normalizarResumen({
      ...(saved.resumenInterno || {}),
      ...auto,
    });
    const coberturaOk = iaDijoCoberturaOk(mensajes) || saved.cobertura === 'Cobertura OK';
    const etapa = inferirEtapaVenta({ resumen: resumenInterno, coberturaOk, savedEtapa: saved.etapa });
    const proximaAccion = inferirProximaAccion({ etapa, coberturaOk, savedProximaAccion: saved.proximaAccion });

    return {
      etapa,
      proximaAccion,
      origen: saved.origen || 'WhatsApp',
      cobertura: saved.cobertura && saved.cobertura !== 'Pendiente de validar'
        ? saved.cobertura
        : coberturaOk
        ? 'Cobertura OK'
        : 'Pendiente de validar',
      prioridadComercial: saved.prioridadComercial || 'Media',
      planSugerido: resumenInterno.plan || saved.planSugerido || '',
      resumenInterno,
      ultimaGestion: saved.ultimaGestion || null,
      observacion: saved.observacion || '',
    };
  };

  const obtenerVenta = (conv) => {
    const local = obtenerVentaLocal(conv);
    if (!conv?.id) return local;

    const backendEtapa = estadoOperativoBackend?.etapa_ventas;
    const etapa = backendEtapa && backendEtapa !== 'Lead nuevo' ? backendEtapa : local?.etapa || 'Lead nuevo';
    const coberturaOk = iaDijoCoberturaOk(mensajes) || local?.cobertura === 'Cobertura OK';
    const proximaAccion =
      estadoOperativoBackend?.proxima_accion ||
      inferirProximaAccion({ etapa, coberturaOk, savedProximaAccion: local?.proximaAccion });

    return {
      ...local,
      etapa,
      proximaAccion,
      observacion: estadoOperativoBackend?.observacion || local?.observacion || '',
    };
  };

  const ventaActual = obtenerVenta(conversacionActiva);

  const construirResumenVenta = (overrideData = {}) => {
    const resumenGuardado = ventaActual?.resumenInterno || {};
    const base = crearFormularioVentaInicial({
      conversacion: conversacionActiva,
      mensajes,
      datosCliente,
      ventaActual,
      resumenGuardado,
      agenteNombre: getNombreAgente(),
    });
    const merged = { ...base, ...overrideData };
    return {
      conversacion_id: conversacionActiva?.id || '',
      cliente_nombre: normalizarTexto(merged.nombre_apellido),
      telefono: normalizarTelefonoVisual(merged.telefono || (conversacionActiva?.telefono?.startsWith('fb_') || conversacionActiva?.telefono?.startsWith('ig_') ? '' : conversacionActiva?.telefono) || ''),
      email: normalizarTexto(merged.email),
      cuil_dni: normalizarTexto(merged.cuil),
      direccion: normalizarTexto(merged.direccion),
      entre_calle_1: normalizarTexto(merged.entre_calle_1),
      entre_calle_2: normalizarTexto(merged.entre_calle_2),
      localidad: normalizarTexto(merged.localidad),
      plan: normalizarTexto(merged.plan || ventaActual?.planSugerido || ''),
      precio: normalizarTexto(merged.precio),
      medio_pago: normalizarTexto(merged.medio_pago),
      coordenadas:
        normalizarTexto(merged.latitud) && normalizarTexto(merged.longitud)
          ? { lat: normalizarTexto(merged.latitud), lng: normalizarTexto(merged.longitud) }
          : null,
      mapa_url: normalizarTexto(merged.mapa_url),
      agente_id: agenteActualId,
      agente_nombre: getNombreAgente(),
      observaciones: normalizarTexto(merged.observaciones),
    };
  };

  const persistirResumenVenta = async (overrideData = {}, patchExtra = {}) => {
    if (!conversacionActiva?.id) return null;
    const resumen = construirResumenVenta(overrideData);
    let coordenadas = resumen?.coordenadas || null;
    let mapaUrl = resumen?.mapa_url || '';
    let direccionCorregida = resumen?.direccion || '';
    let localidadCorregida = resumen?.localidad || '';

    const latValida = coordenadas?.lat && !Number.isNaN(parseFloat(coordenadas.lat));
    const lngValida = coordenadas?.lng && !Number.isNaN(parseFloat(coordenadas.lng));

    if (!latValida || !lngValida) {
      const geocodeResponse = await geocodificarResumenVentaFrontend(
        {
          direccion: resumen?.direccion,
          entre_calle_1: resumen?.entre_calle_1,
          entre_calle_2: resumen?.entre_calle_2,
          localidad: resumen?.localidad,
        },
        authFetch
      );
      const geocode = geocodeResponse?.coordenadas;
      if (geocode?.lat && geocode?.lng) {
        coordenadas = { lat: String(geocode.lat), lng: String(geocode.lng) };
        mapaUrl = geocodeResponse?.google_maps_url || geocodeResponse?.mapa_url || '';
        if (geocodeResponse?.direccion_canonica) direccionCorregida = geocodeResponse.direccion_canonica;
        if (geocodeResponse?.localidad_canonica) localidadCorregida = geocodeResponse.localidad_canonica;
      }
    }

    const resumenInternoFinal = normalizarResumen({
      ...(ventaActual?.resumenInterno || {}),
      ...overrideData,
      nombre_apellido: resumen.cliente_nombre,
      cuil: resumen.cuil_dni,
      email: resumen.email,
      telefono: resumen.telefono,
      direccion: direccionCorregida || overrideData?.direccion || ventaActual?.resumenInterno?.direccion || '',
      localidad: localidadCorregida || overrideData?.localidad || ventaActual?.resumenInterno?.localidad || '',
      entre_calle_1: resumen.entre_calle_1,
      entre_calle_2: resumen.entre_calle_2,
      plan: resumen.plan,
      precio: resumen.precio,
      medio_pago: resumen.medio_pago,
      latitud: coordenadas?.lat || overrideData?.latitud || ventaActual?.resumenInterno?.latitud || '',
      longitud: coordenadas?.lng || overrideData?.longitud || ventaActual?.resumenInterno?.longitud || '',
      mapa_url: mapaUrl || overrideData?.mapa_url || ventaActual?.resumenInterno?.mapa_url || '',
      observaciones: resumen.observaciones,
    });

    actualizarVentaLocal(conversacionActiva.id, {
      resumenInterno: resumenInternoFinal,
      planSugerido: resumen.plan || ventaActual?.planSugerido || '',
      ...patchExtra,
      ultimaGestion: new Date().toISOString(),
    });

    return {
      ...resumen,
      direccion: direccionCorregida || resumen.direccion,
      localidad: localidadCorregida || resumen.localidad,
      coordenadas,
      mapa_url: mapaUrl,
    };
  };

  const abrirModalVentas = (type) => {
    if (!conversacionActiva?.id) return;
    const resumenBase = crearFormularioVentaInicial({
      conversacion: conversacionActiva,
      mensajes,
      datosCliente,
      ventaActual,
      resumenGuardado: ventaActual?.resumenInterno || {},
      agenteNombre: getNombreAgente(),
    });

    if (type === 'confirmar') {
      setSalesModal({ open: true, type });
      setSalesModalForm({ ...resumenBase, proxima_accion: ventaActual?.proximaAccion || 'Pendiente de instalaciÃ³n' });
      return;
    }

    const presets = {
      contactar: { observacion: ventaActual?.observacion || '' },
      calificar: { observacion: ventaActual?.observacion || '' },
      cobertura: {
        direccion: resumenBase.direccion,
        localidad: resumenBase.localidad,
        observacion: ventaActual?.observacion || '',
        cobertura_resultado:
          ventaActual?.cobertura === 'Sin cobertura expansiÃ³n'
            ? 'sin_cobertura_expansion'
            : ventaActual?.cobertura === 'Sin cobertura'
            ? 'sin_cobertura'
            : 'cobertura_ok',
      },
      propuesta: {
        plan: resumenBase.plan,
        precio: resumenBase.precio,
        medio_pago: resumenBase.medio_pago,
        observacion: ventaActual?.observacion || '',
        localidad: resumenBase.localidad,
        contexto_catalogo: '',
        regla_facturacion_catalogo: '',
        productos_count_catalogo: 0,
        catalogo_error: '',
        catalogo_loading: true,
      },
      seguimiento: { proxima_accion: ventaActual?.proximaAccion || '', fecha_seguimiento: '', observacion: ventaActual?.observacion || '' },
      aceptar: { plan: resumenBase.plan, precio: resumenBase.precio, observacion: ventaActual?.observacion || '' },
      perdida: { motivo_perdida: '', observacion: '' },
    };

    setSalesModal({ open: true, type });
    setSalesModalForm(presets[type] || {});

    if (type === 'propuesta') {
      cargarContextoCatalogoVentas(resumenBase.localidad).then((catalogo) => {
        setSalesModalForm((prev) => ({
          ...prev,
          ...catalogo,
          catalogo_loading: false,
        }));
      });
    }
  };

  const cerrarModalVentas = () => {
    setSalesModal({ open: false, type: null });
    setSalesModalForm({});
  };

  const guardarCambioVentas = async ({ etapa, proximaAccion, observacion, patch = {} }) => {
    if (!conversacionActiva?.id) return false;
    actualizarVentaLocal(conversacionActiva.id, {
      etapa,
      proximaAccion,
      observacion,
      ...patch,
      ultimaGestion: new Date().toISOString(),
    });
    await guardarEstadoOperativo({ area: 'ventas', campo: 'etapa_ventas', valor: etapa, proxima_accion: proximaAccion, observacion });
    return true;
  };

  const guardarResumenVenta = async (overrideData = {}) => {
    if (!conversacionActiva?.id) return false;
    try {
      setSubmittingSalesModal(true);
      const resumen = await persistirResumenVenta(overrideData, {
        etapa: ventaActual?.etapa || 'Lead nuevo',
        proximaAccion: ventaActual?.proximaAccion || 'Revisar resumen interno',
      });
      const nombreCompleto = normalizarTexto(resumen?.cliente_nombre);
      const localidad = normalizarTexto(resumen?.localidad);
      await guardarCambioVentas({
        etapa: ventaActual?.etapa || 'Lead nuevo',
        proximaAccion: nombreCompleto && localidad ? 'Resumen interno listo para confirmaciÃ³n' : 'Completar datos faltantes para cierre',
        observacion: resumen?.observaciones || ventaActual?.observacion || 'Resumen interno actualizado',
        patch: {
          resumenInterno: {
            ...DEFAULT_SALES_SUMMARY,
            ...(ventaActual?.resumenInterno || {}),
            ...overrideData,
            latitud: resumen?.coordenadas?.lat || '',
            longitud: resumen?.coordenadas?.lng || '',
            mapa_url: resumen?.mapa_url || '',
          },
          planSugerido: resumen?.plan || ventaActual?.planSugerido || '',
        },
      });
      cerrarModalVentas();
      alert('âœ… Resumen interno guardado. La venta todavÃ­a no fue confirmada.');
      return true;
    } catch (error) {
      console.error('Error guardando resumen interno:', error);
      alert('âŒ Error guardando resumen interno');
      return false;
    } finally {
      setSubmittingSalesModal(false);
    }
  };

  const confirmarVenta = async (overrideData = {}) => {
    if (!conversacionActiva?.id) return false;
    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return false;
    }
    try {
      setSubmittingSalesModal(true);
      const resumen = await persistirResumenVenta(overrideData, {
        etapa: 'Venta confirmada',
        proximaAccion: 'Pendiente de instalaciÃ³n',
        cobertura: 'Cobertura OK',
      });
      const faltantes = [];
      if (!normalizarTexto(resumen?.cliente_nombre)) faltantes.push('Cliente');
      if (!normalizarTexto(resumen?.telefono)) faltantes.push('TelÃ©fono');
      if (!normalizarTexto(resumen?.cuil_dni)) faltantes.push('CUIL/DNI');
      if (!normalizarTexto(resumen?.direccion)) faltantes.push('DirecciÃ³n');
      if (!normalizarTexto(resumen?.localidad)) faltantes.push('Localidad');
      if (!normalizarTexto(resumen?.plan)) faltantes.push('Plan');
      if (!normalizarTexto(resumen?.precio)) faltantes.push('Precio');
      if (!normalizarTexto(resumen?.medio_pago)) faltantes.push('Medio de pago');

      if (faltantes.length) {
        alert(`Faltan datos obligatorios para confirmar la venta:\n- ${faltantes.join('\n- ')}`);
        return false;
      }

      const payloadFinal = {
        ...resumen,
        coordenadas: resumen.coordenadas || null,
        mapa_url: resumen.mapa_url || '',
      };

      const response = await authFetch(`${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/confirmar-venta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadFinal),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(`âŒ Error: ${data?.error || 'No se pudo confirmar la venta'}`);
        return false;
      }

      await guardarCambioVentas({
        etapa: 'Venta confirmada',
        proximaAccion: 'Pendiente de instalaciÃ³n',
        observacion: resumen.observaciones || 'Venta confirmada por agente',
        patch: { cobertura: 'Cobertura OK', planSugerido: resumen.plan || ventaActual?.planSugerido || '' },
      });
      await cargarConversaciones();
      cerrarModalVentas();
      alert('âœ… Venta confirmada y enviada a Telegram');
      return true;
    } catch (error) {
      console.error('Error confirmando venta:', error);
      alert('âŒ Error de conexiÃ³n confirmando venta');
      return false;
    } finally {
      setSubmittingSalesModal(false);
    }
  };

  const guardarAccionVentasModal = async () => {
    if (!conversacionActiva?.id) return;
    const tipo = salesModal.type;
    if (!tipo) return;
    try {
      setSubmittingSalesModal(true);
      if (tipo === 'confirmar') {
        await confirmarVenta(salesModalForm);
        return;
      }
      if (tipo === 'contactar') {
        await guardarCambioVentas({ etapa: 'Contactado', proximaAccion: salesModalForm.proxima_accion || 'Continuar calificaciÃ³n comercial', observacion: salesModalForm.observacion || 'Contacto inicial realizado' });
      }
      if (tipo === 'calificar') {
        const ed = salesModalForm.resultado_calificacion === 'descartar' ? 'Venta perdida' : 'Calificado';
        await guardarCambioVentas({ etapa: ed, proximaAccion: ed === 'Venta perdida' ? 'Caso cerrado' : salesModalForm.proxima_accion || 'Validar cobertura', observacion: salesModalForm.observacion || '' });
      }
      if (tipo === 'cobertura') {
        await persistirResumenVenta({
          direccion: salesModalForm.direccion || '',
          entre_calle_1: salesModalForm.entre_calle_1 || '',
          entre_calle_2: salesModalForm.entre_calle_2 || '',
          localidad: salesModalForm.localidad || '',
          observaciones: salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        });
        let ed = 'Cobertura validada';
        let pa = 'Enviar propuesta';
        let cob = 'Cobertura OK';
        if (salesModalForm.cobertura_resultado === 'sin_cobertura_expansion') {
          ed = 'Sin cobertura expansiÃ³n';
          pa = 'Esperar avance de red';
          cob = 'Sin cobertura expansiÃ³n';
        } else if (salesModalForm.cobertura_resultado === 'sin_cobertura') {
          ed = 'Venta perdida';
          pa = 'Caso cerrado';
          cob = 'Sin cobertura';
        }
        await guardarCambioVentas({ etapa: ed, proximaAccion: pa, observacion: salesModalForm.observacion || '', patch: { cobertura: cob } });
      }
      if (tipo === 'propuesta') {
        await persistirResumenVenta({
          plan: salesModalForm.plan || '',
          precio: salesModalForm.precio || '',
          medio_pago: salesModalForm.medio_pago || '',
          observaciones: salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        }, { planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '' });
        await guardarCambioVentas({ etapa: 'Propuesta enviada', proximaAccion: salesModalForm.proxima_accion || 'Hacer seguimiento comercial', observacion: salesModalForm.observacion || '', patch: { planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '' } });
      }
      if (tipo === 'seguimiento') {
        await guardarCambioVentas({ etapa: 'Seguimiento', proximaAccion: salesModalForm.proxima_accion || 'Recontactar cliente', observacion: salesModalForm.observacion || '' });
      }
      if (tipo === 'aceptar') {
        await persistirResumenVenta({
          plan: salesModalForm.plan || '',
          precio: salesModalForm.precio || '',
          observaciones: salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        }, { planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '' });
        await guardarCambioVentas({ etapa: 'AceptÃ³ oferta', proximaAccion: 'Validar datos finales y confirmar venta', observacion: salesModalForm.observacion || '', patch: { planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '' } });
      }
      if (tipo === 'perdida') {
        await guardarCambioVentas({ etapa: 'Venta perdida', proximaAccion: 'Caso cerrado', observacion: `${salesModalForm.motivo_perdida || 'Sin motivo'}${salesModalForm.observacion ? ` Â· ${salesModalForm.observacion}` : ''}` });
      }
      await cargarConversaciones();
      cerrarModalVentas();
    } catch (error) {
      console.error('Error guardando acciÃ³n de ventas:', error);
      alert('âŒ No se pudo guardar la acciÃ³n comercial');
    } finally {
      setSubmittingSalesModal(false);
    }
  };

  const accionRapidaVentas = async (accion) => {
    const tipo = {
      contactar: 'contactar',
      calificar: 'calificar',
      cobertura: 'cobertura',
      propuesta: 'propuesta',
      seguimiento: 'seguimiento',
      aceptar: 'aceptar',
      perdida: 'perdida',
      confirmar: 'confirmar',
    }[accion];
    if (tipo) abrirModalVentas(tipo);
  };

  return {
    ventasLocalData,
    salesModal,
    salesModalForm,
    setSalesModalForm,
    submittingSalesModal,
    ventaActual,
    actualizarVentaLocal,
    obtenerVentaLocal,
    obtenerVenta,
    abrirModalVentas,
    cerrarModalVentas,
    confirmarVenta,
    guardarAccionVentasModal,
    accionRapidaVentas,
    guardarResumenVenta,
  };
}

